import React from 'react'
import { useMutation } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import type { PromptResolve } from '@ur-solutions/react-components'
import { useToast } from '@ur-solutions/react-components'
import { RegularModal, DateRangePicker, Select } from '@/components_deprecated'
import { startOfMonth, endOfMonth } from 'date-fns'
import { EXPORT_FORM_RESPONSES } from '../mutations'
import { ExportFormat } from '@/modules/exports'

interface ExportPromptProps {
  formId: string
  onResolve: PromptResolve<boolean>
}

export const ExportPrompt: React.FC<ExportPromptProps> = ({
  formId,
  onResolve,
}) => {
  const translations = useTranslate({
    startDate: 'common.start-date',
    endDate: 'common.end-date',
    format: 'common.format',
    export: 'common.export',
    cancel: 'common.cancel',
    success: 'exports.success-user-exports',
    dateRange: 'common.range',
  })

  const addToast = useToast()

  const { formValues, formChangeHandler: handler } = useForm({
    values: {
      dateRange: [startOfMonth(new Date()), endOfMonth(new Date())] as [
        Date,
        Date,
      ],
      format: ExportFormat.EXCEL,
    },
  })

  const [exportFormResponses, { loading }] = useMutation(
    EXPORT_FORM_RESPONSES,
    {
      onCompleted() {
        addToast(
          'success',
          translations.success || 'Export initiated successfully'
        )
        onResolve(true)
      },
      onError(e) {
        // Can't use onErrorAuto here cause it's technically outside of graph context
        addToast('error', 'Export failed')
        console.error(e)
      },
    }
  )

  const handleSubmit = () => {
    const [startDate, endDate] = formValues.dateRange

    void exportFormResponses({
      variables: {
        formTemplateId: formId,
        dateStart: startDate.toISOString().split('T')[0],
        dateEnd: endDate.toISOString().split('T')[0],
        exportFormat: formValues.format,
      },
    })
  }

  const formatOptions = [{ value: ExportFormat.EXCEL, label: 'Excel' }]

  const handleFormatChange = (value: ExportFormat | null) => {
    if (value) {
      handler('format')(value)
    }
  }

  return (
    <RegularModal
      title="Export Form Responses"
      loading={loading}
      onCancel={() => onResolve(false)}
      onSubmit={handleSubmit}
      submitText={translations.export || 'Export'}
      cancelText={translations.cancel || 'Cancel'}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label>{translations.dateRange || 'Date Range'}</label>
          <DateRangePicker
            value={formValues.dateRange}
            onChange={handler('dateRange')}
          />
        </div>

        <div>
          <label>{translations.format || 'Format'}</label>
          <Select
            options={formatOptions}
            value={formValues.format}
            onChange={handleFormatChange}
          />
        </div>
      </div>
    </RegularModal>
  )
}
